import * as React from 'react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { ChapterPicker } from '@/pages/JoinFormChapterPicker'
import { DiscountCodeSection } from '@/pages/PublicJoinDiscountCode'
import { FieldShell, PublicCustomFieldControl } from '@/pages/PublicJoinFormPage.fields'
import { OrganizationFieldsSection } from '@/pages/PublicJoinFormPage.organization'
import {
  autoCompleteForPersonField,
  customFieldsForLevel,
  personFieldTypes,
} from '@/pages/PublicJoinFormPage.helpers'
import type {
  JoinCustomFieldValue,
  DiscountQuote,
  JoinFormErrors,
  JoinOptionsResponse,
  OrganizationForm,
  PersonFieldName,
  PersonForm,
  PublicJoinCustomField,
  PublicMembershipLevel,
} from '@/pages/PublicJoinFormPage.types'
import type { Association } from '@/pages/StaffSignInPage'

export function PublicJoinShell({
  association,
  children,
}: {
  association: Association
  children: React.ReactNode
}) {
  return (
    <main className="min-h-screen bg-background px-6 py-6">
      <div className="mb-5 flex items-center gap-2.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-body-sm text-primary-foreground">
          {association.name.slice(0, 1)}
        </span>
        <span className="text-body-sm text-foreground">{association.name}</span>
      </div>
      <div className="w-full max-w-2xl">{children}</div>
    </main>
  )
}

export function ApplyStep({
  associationName,
  options,
  selectedLevelId,
  person,
  organization,
  customFieldValues,
  errors,
  selectedLevel,
  discountCode,
  discountQuote,
  discountPending,
  discountError,
  onSelectLevel,
  onPersonChange,
  onOrganizationChange,
  onCustomFieldChange,
  chapterIds,
  onChapterIdsChange,
  onDiscountCodeChange,
  onApplyDiscount,
  onReview,
}: {
  associationName: string
  options: JoinOptionsResponse
  selectedLevelId: number | null
  person: PersonForm
  organization: OrganizationForm
  customFieldValues: Record<string, JoinCustomFieldValue>
  errors: JoinFormErrors
  selectedLevel: PublicMembershipLevel | null
  discountCode: string
  discountQuote: DiscountQuote | null
  discountPending: boolean
  discountError?: string
  onSelectLevel: (levelId: number) => void
  onPersonChange: (name: PersonFieldName, value: string) => void
  onOrganizationChange: (name: PersonFieldName, value: string) => void
  onCustomFieldChange: (field: PublicJoinCustomField, value: JoinCustomFieldValue) => void
  chapterIds: number[]
  onChapterIdsChange: (ids: number[]) => void
  onDiscountCodeChange: (value: string) => void
  onApplyDiscount: () => void
  onReview: () => void
}) {
  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-subheading font-semibold text-foreground">Join {associationName}</h1>
        <p className="mt-1 text-body-sm text-muted-foreground">
          Choose a Membership Level, enter your details, and review before submitting.
        </p>
      </header>
      {errors.form && <Alert>{errors.form}</Alert>}
      <MembershipLevelSection
        levels={options.membership_levels}
        selectedLevelId={selectedLevelId}
        error={errors.membershipLevel}
        onSelectLevel={onSelectLevel}
      />
      {selectedLevel?.kind === 'organization' && (
        <OrganizationFieldsSection
          organization={organization}
          errors={errors.organization}
          onChange={onOrganizationChange}
        />
      )}
      {selectedLevel && (selectedLevel.discount_code_available ?? !selectedLevel.free) && (
        <DiscountCodeSection
          level={selectedLevel}
          code={discountCode}
          quote={discountQuote}
          pending={discountPending}
          error={discountError}
          onCodeChange={onDiscountCodeChange}
          onApply={onApplyDiscount}
        />
      )}
      <IdentityFieldsSection
        fields={options.identity_fields}
        person={person}
        errors={errors}
        onPersonChange={onPersonChange}
      />
      <ChapterPicker
        setting={options.chapter_setting}
        chapters={options.chapters ?? []}
        selectedIds={chapterIds}
        error={errors.chapters}
        onChange={onChapterIdsChange}
      />
      <CustomFieldsSection
        fields={customFieldsForLevel(options, selectedLevel)}
        values={customFieldValues}
        errors={errors}
        onChange={onCustomFieldChange}
      />
      <div className="flex justify-start pt-1">
        <Button type="button" onClick={onReview}>
          Review application
        </Button>
      </div>
    </div>
  )
}

function MembershipLevelSection({
  levels,
  selectedLevelId,
  error,
  onSelectLevel,
}: {
  levels: PublicMembershipLevel[]
  selectedLevelId: number | null
  error?: string
  onSelectLevel: (levelId: number) => void
}) {
  return (
    <section className="space-y-3" aria-labelledby="join-level-heading">
      <div>
        <h2 id="join-level-heading" className="text-body font-semibold text-foreground">
          Membership Level
        </h2>
        {error && <p className="mt-1 text-caption text-destructive">{error}</p>}
      </div>
      {levels.length === 0 ? (
        <p className="rounded-md border border-border bg-card px-3 py-2 text-body-sm text-muted-foreground">
          No Membership Levels are currently available.
        </p>
      ) : (
        <div className="grid gap-2">
          {levels.map((level) => (
            <LevelOption
              key={level.id}
              level={level}
              selected={level.id === selectedLevelId}
              onSelect={() => onSelectLevel(level.id)}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function IdentityFieldsSection({
  fields,
  person,
  errors,
  onPersonChange,
}: {
  fields: JoinOptionsResponse['identity_fields']
  person: PersonForm
  errors: JoinFormErrors
  onPersonChange: (name: PersonFieldName, value: string) => void
}) {
  return (
    <section className="space-y-4" aria-labelledby="join-details-heading">
      <h2 id="join-details-heading" className="text-body font-semibold text-foreground">
        Your Details
      </h2>
      {fields.map((field) => (
        <FieldShell
          key={field.name}
          id={`join-${field.name}`}
          label={field.label}
          required={field.required}
          error={errors.person[field.name]}
        >
          <Input
            id={`join-${field.name}`}
            type={personFieldTypes[field.name]}
            value={person[field.name]}
            autoComplete={autoCompleteForPersonField(field.name)}
            aria-invalid={Boolean(errors.person[field.name])}
            onChange={(event) => onPersonChange(field.name, event.target.value)}
          />
        </FieldShell>
      ))}
    </section>
  )
}

function CustomFieldsSection({
  fields,
  values,
  errors,
  onChange,
}: {
  fields: PublicJoinCustomField[]
  values: Record<string, JoinCustomFieldValue>
  errors: JoinFormErrors
  onChange: (field: PublicJoinCustomField, value: JoinCustomFieldValue) => void
}) {
  if (fields.length === 0) return null

  return (
    <section className="space-y-4" aria-labelledby="join-fields-heading">
      <h2 id="join-fields-heading" className="text-body font-semibold text-foreground">
        Additional Questions
      </h2>
      {fields.map((field) => (
        <PublicCustomFieldControl
          key={field.id}
          field={field}
          value={values[String(field.id)]}
          error={errors.customFields[String(field.id)]}
          onChange={(value) => onChange(field, value)}
        />
      ))}
    </section>
  )
}

function LevelOption({
  level,
  selected,
  onSelect,
}: {
  level: PublicMembershipLevel
  selected: boolean
  onSelect: () => void
}) {
  return (
    <Button
      type="button"
      variant={selected ? 'default' : 'outline'}
      aria-pressed={selected}
      className={cn(
        'h-auto min-h-14 w-full justify-start px-3 py-3 text-left',
        selected ? 'text-primary-foreground' : 'bg-card',
      )}
      onClick={onSelect}
    >
      <span className="flex w-full flex-col gap-1">
        <span className="flex items-center justify-between gap-3">
          <span className="text-body-sm font-semibold">{level.name}</span>
          <span className="text-body-sm font-normal">{level.price_label ?? level.fee}</span>
        </span>
        <span
          className={cn(
            'text-caption font-normal',
            selected ? 'text-primary-foreground/80' : 'text-muted-foreground',
          )}
        >
          {level.renewal}
          {level.requires_admin_approval ? ' · approval required' : ''}
        </span>
        {level.description && (
          <span
            className={cn(
              'text-caption font-normal',
              selected ? 'text-primary-foreground/80' : 'text-muted-foreground',
            )}
          >
            {level.description}
          </span>
        )}
      </span>
    </Button>
  )
}

/* ReviewStep moved to PublicJoinFormPage.review.tsx. */
/*
export function ReviewStep({
  selectedLevel,
  organization,
  person,
  customFields,
  customFieldValues,
  website,
  submitting,
  discountQuote,
  enrollAutopay,
  onBack,
  onWebsiteChange,
  onEnrollAutopayChange,
  onSubmit,
}: {
  selectedLevel: PublicMembershipLevel
  organization: OrganizationForm
  person: PersonForm
  customFields: PublicJoinCustomField[]
  customFieldValues: Record<string, JoinCustomFieldValue>
  website: string
  submitting: boolean
  discountQuote: DiscountQuote | null
  enrollAutopay: boolean
  onBack: () => void
  onWebsiteChange: (value: string) => void
  onEnrollAutopayChange: (checked: boolean) => void
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void
}) {
  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      <header>
        <h1 className="text-subheading font-semibold text-foreground">Review application</h1>
        <p className="mt-1 text-body-sm text-muted-foreground">
          Confirm these details before submitting to the association.
        </p>
      </header>
      <SummarySection title="Membership Level">
        <SummaryRow label="Level" value={selectedLevel.name} />
        <SummaryRow
          label="Fee"
          value={
            selectedLevel.free ? selectedLevel.fee : (selectedLevel.subtotal ?? selectedLevel.fee)
          }
        />
        {selectedLevel.tax_rate && (
          <SummaryRow
            label={`Tax — ${selectedLevel.tax_rate.name} (${selectedLevel.tax_rate.percentage}%)`}
            value={selectedLevel.tax}
          />
        )}
        {discountQuote && (
          <>
            <SummaryRow
              label="Discount Code"
              value={`${discountQuote.code} (${discountReductionLabel(discountQuote)})`}
            />
            <SummaryRow label="Reduction" value={`−${discountQuote.reduction}`} />
            <SummaryRow label="Total" value={discountQuote.total} />
          </>
        )}
        {!discountQuote && selectedLevel.tax_rate && (
          <SummaryRow label="Total" value={selectedLevel.total} />
        )}
        <SummaryRow label="Renewal" value={selectedLevel.renewal} />
      </SummarySection>
      <SummarySection title="Your Details">
        <SummaryRow label="Name" value={person.name} />
        <SummaryRow label="Email" value={person.email || 'None'} />
        <SummaryRow label="Phone" value={person.phone || 'None'} />
      </SummarySection>
      <OrganizationSummary level={selectedLevel} organization={organization} />
      {customFields.length > 0 && (
        <SummarySection title="Additional Questions">
          {customFields.map((field) => (
            <SummaryRow
              key={field.id}
              label={field.label}
              value={displayCustomFieldValue(customFieldValues[String(field.id)])}
            />
          ))}
        </SummarySection>
      )}
      {selectedLevel.autopay_offer && (
        <AutopayConsentChoice
          offer={selectedLevel.autopay_offer}
          checked={enrollAutopay}
          onCheckedChange={onEnrollAutopayChange}
        />
      )}
      <input
        type="text"
        name="website"
        value={website}
        autoComplete="off"
        tabIndex={-1}
        aria-hidden="true"
        className="hidden"
        onChange={(event) => onWebsiteChange(event.target.value)}
      />
      <div className="flex gap-2 pt-1">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Submitting...' : 'Submit application'}
        </Button>
      </div>
    </form>
  )
}
*/
